import { cn } from '@lib/utils';
import React, { PropsWithChildren, useCallback, useRef, useState } from 'react';
import { EmptyStateContainer, EmptyStateContainerProps } from './empty-state-container.component';
import { LoadingStateContainer, LoadingStateContainerProps } from './loading-state-container.component';
import { TileHoverContext } from './tile-context';
import { TileHeader, TileHeaderProps } from './tile-header.component';

export type TileProps = {
  className?: string;
  contentClassName?: string;
  description?: string;
  /**
   * Used with `isEmpty`. When the tile is empty, Tile renders only this
   * EmptyStateContainer and suppresses the normal header, description, actions,
   * and children so the empty state owns the title and CTA.
   */
  emptyState?: EmptyStateContainerProps;
  header?: TileHeaderProps;
  /**
   * When true, Tile manages hover state internally and provides it via context.
   * Children can use `useTileHover()` or `IconButton.HoverReveal` to react to hover.
   * Header iconButton will auto-receive `isVisible` from hover state.
   */
  hoverReveal?: boolean;
  isEmpty?: boolean;
  isLoading?: boolean;
  loadingState?: LoadingStateContainerProps;
  /**
   * @deprecated Use `hoverReveal` instead for automatic hover state management.
   * Manual mouse handlers still work for backwards compatibility.
   */
  onMouseEnter?: () => void;
  /**
   * @deprecated Use `hoverReveal` instead for automatic hover state management.
   * Manual mouse handlers still work for backwards compatibility.
   */
  onMouseLeave?: () => void;
};

export const Tile: React.FC<PropsWithChildren<TileProps>> = (props) => {
  const {
    children,
    className,
    contentClassName,
    description,
    emptyState,
    header,
    hoverReveal = false,
    isEmpty = false,
    isLoading = false,
    loadingState,
    onMouseEnter,
    onMouseLeave,
  } = props;

  const [isHovering, setIsHovering] = useState(false);
  // Track active count (ref-counted for multiple dropdowns)
  const activeCountRef = useRef(0);
  const [isActive, setIsActiveState] = useState(false);

  const setActive = useCallback((active: boolean) => {
    if (active) {
      activeCountRef.current += 1;
    } else {
      activeCountRef.current = Math.max(0, activeCountRef.current - 1);
    }
    setIsActiveState(activeCountRef.current > 0);
  }, []);

  const handleMouseEnter = (): void => {
    if (hoverReveal) {
      setIsHovering(true);
    }
    onMouseEnter?.();
  };

  const handleMouseLeave = (): void => {
    if (hoverReveal) {
      setIsHovering(false);
    }
    onMouseLeave?.();
  };

  // An empty tile should never show its own title/description — the EmptyStateContainer
  // owns the heading and CTA. Suppress the header whenever the tile's body is an empty state,
  // whether routed through `isEmpty`/`emptyState` or rendered directly as the only child.
  const childIsEmptyState = React.isValidElement(children) && children.type === EmptyStateContainer;

  // When hoverReveal is enabled and header has iconButton, auto-inject isVisible
  // Use isHovering || isActive so icon stays visible when dropdown is open
  const headerWithHover: TileHeaderProps | undefined =
    hoverReveal && header?.iconButton
      ? {
          ...header,
          iconButton: {
            ...header.iconButton,
            // Only auto-set isVisible if not explicitly provided
            isVisible: header.iconButton.isVisible ?? (isHovering || isActive),
          },
        }
      : header;

  const content = (
    <div
      className={cn('flex flex-col justify-start bg-white p-4 rounded-xl h-fit shadow-sm', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isLoading && <LoadingStateContainer {...loadingState} />}

      {!isLoading && isEmpty && <EmptyStateContainer {...emptyState} />}

      {!isLoading && !isEmpty && (
        <div className={cn('flex flex-col h-fit w-full gap-4', contentClassName)}>
          {!childIsEmptyState && (headerWithHover || description) && (
            <section className="flex flex-col gap-1">
              {headerWithHover && <TileHeader {...headerWithHover} />}
              {description && <p className="text-sm text-gray-500">{description}</p>}
            </section>
          )}
          {children}
        </div>
      )}
    </div>
  );

  // Wrap with context provider when hoverReveal is enabled
  if (hoverReveal) {
    return <TileHoverContext.Provider value={{ isActive, isHovering, setActive }}>{content}</TileHoverContext.Provider>;
  }

  return content;
};
